import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { useFocusWithin } from '@react-aria/interactions'
import Image from 'next/image'
import React from 'react'

import { formControlReset } from 'styles/reset'

type Props = {
	avatarURLs: string[]
	watchValue: () => string
	onChange: (index: number) => void
}

const BackgroundBox = styled.div`
	padding: 8px;
	width: 100%;
	max-width: 380px;
	display: flex;
	overflow: auto;
	flex-direction: row;
	align-items: baseline;
	justify-content: space-between;
	border-radius: ${({ theme }) => theme.borderRadiuses.md};
	background-color: ${({ theme }) => theme.colors.light.surfaceDecorative};

	${({ theme }) =>
		theme.isMobile
			? css`
					gap: 0px;
			  `
			: css`
					gap: 4px;
					height: 130px;
					flex-wrap: wrap;
			  `};
`

const ImageWrapper = styled.button<{ isSelected: boolean }>`
	${formControlReset};
	cursor: pointer;
	display: flex;
	justify-content: center;
	align-items: center;
	will-change: transform;
	padding: ${({ theme }) => theme.spaces.space2};
	border-radius: ${({ theme }) => theme.borderRadiuses.round};
	border: 1px solid ${({ theme, isSelected }) => (isSelected ? theme.colors.light.surfaceInteractive : 'transparent')};
	transition: transform 200ms ${({ theme }) => theme.transitions.easings.css.spring},
		border 150ms ${({ theme }) => theme.transitions.easings.css.smooth};

	@media (hover: hover) {
		&:hover {
			transform: scale(1.1);
		}
	}
`

const imageStyles = css`
	border-radius: 50%;
`

function AvatarGallery({ avatarURLs, watchValue, onChange }: Props) {
	const value = watchValue()
	const boxRef = React.useRef<HTMLDivElement>(null)
	const [isFocusWithin, setIsFocusWithin] = React.useState(false)
	const { focusWithinProps } = useFocusWithin({
		onBlurWithin: () => setIsFocusWithin(false),
	})

	const handleBoxKeyDown = (event: React.KeyboardEvent) => {
		if (event.code === 'Space') {
			event.preventDefault()
		}
	}

	const handleBoxKeyUp = (event: React.KeyboardEvent) => {
		if (event.code === 'Space') {
			event.preventDefault()
			setIsFocusWithin(true)
			if (boxRef.current) {
				const [button] = boxRef.current.getElementsByTagName('button')
				button.focus()
			}
		}
	}

	const handleImageKeyUp = (event: React.KeyboardEvent) => {
		if (event.code === 'Escape') {
			setIsFocusWithin(false)
			if (boxRef.current) {
				boxRef.current.focus()
			}
		}
	}

	return (
		<BackgroundBox
			role="grid"
			ref={boxRef}
			tabIndex={0}
			onKeyDown={handleBoxKeyDown}
			onKeyUp={handleBoxKeyUp}
			{...focusWithinProps}
		>
			{avatarURLs.map((url, index) => (
				<ImageWrapper
					key={url}
					type="button"
					role="gridcell"
					isSelected={url === value}
					tabIndex={isFocusWithin ? 0 : -1}
					onKeyUp={handleImageKeyUp}
					onClick={() => onChange(index)}
				>
					<Image draggable={false} src={url} width="50" height="50" alt="avatar" css={imageStyles} />
				</ImageWrapper>
			))}
		</BackgroundBox>
	)
}

export default AvatarGallery
